import { useEffect, useState } from "react";
import { api } from "../../../api/api";
import {
  InputCheckBox,
  InputDate,
  InputFile,
  InputSelect,
  InputText,
  InputTextBox,
} from "../../../components/Inputs/Inputs";
import Modal from "../../../components/Modal/Modal";
import { useToast } from "../../../components/Toast";
import { pls_docs_url, pls_docs_master_url } from "../../../api/urls";
import unknownError from "../../../utils/unknownError";
import { idToname } from "../../../utils/idToname";
import { nameToid } from "../../../utils/nameToid";
import { urlTofile } from "../../../utils/urlTofile";
import objToFormdata from "../../../utils/objToFormdata";
import { moveUpToError } from "../../../utils/moveUpToError";

const initialState = {
  code: "",
  printable_name: "",
  description: "",
  doc_name: "",
  doc_number: "",
  doc_expiry: "",
  doc_file: "",
  is_verified: false,
  doc_kyc: false,
  doc_certificate: true,
  doc_type: "",
};

const NewPLSCertModal = ({ trigger, refetch, editData, delivery_boy_id }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const toast = useToast();

  useEffect(() => {
    if (editData) {
      const tmp = async () => {
        const newData = { ...editData };
        newData.doc_file = await urlTofile(newData.doc_file);
        setData(newData);
      };
      tmp();
    }
  }, [editData]);

  useEffect(() => {
    if (open === false && !editData) {
      setData({ ...initialState });
      setErrors({});
    }
  }, [open, setData, setErrors, editData]);

  const [kyccert, setKyccert] = useState();
  useEffect(() => {
    const get = async () => {
      try {
        const res = await api.get(pls_docs_master_url + "?doc=certify");
        setKyccert(res.data);
      } catch (err) {
        unknownError(err);
      }
    };
    get();
  }, []);

  const set = (key, value) => {
    setData((d) => {
      const newData = { ...d };
      newData[key] = value;
      return newData;
    });
  };

  const view = false;

  return (
    <Modal
      open={open}
      onOpen={() => setOpen(true)}
      trigger={trigger}
      className="modal-window"
      overlayClassName="no-overlay"
    >
      <form
        className="modal-content"
        onSubmit={(e) => {
          e.preventDefault();
          const newData = { ...data };
          newData["delivery_boy"] = delivery_boy_id;
          try {
            if (editData) {
              api
                .patch(pls_docs_url + data.id + "/", objToFormdata(newData))
                .then((res) => {
                  refetch();
                  setOpen(false);
                  toast.open("Edited data with id: " + res.data.id, "success");
                })
                .catch((err) => {
                  if (err?.response?.data) {
                    setErrors(err.response.data);
                    moveUpToError();
                  } else {
                    unknownError(err);
                  }
                });
            } else {
              api
                .post(pls_docs_url, objToFormdata(newData))
                .then((res) => {
                  refetch();
                  setOpen(false);
                  toast.open("Data added successfully", "success");
                })
                .catch((err) => {
                  if (err?.response?.data) {
                    setErrors(err.response.data);
                    moveUpToError();
                  } else {
                    unknownError(err);
                  }
                });
            }
          } catch (err) {
            unknownError(err);
          }
        }}
      >
        <div className="modal-heading">KYC Certificate</div>
        <div className="modal-form">
          <div id="form-wrapper">
            <div className="twoside-form">
              <div className="formside">
                <InputText
                  label="Code"
                  placeholder="Code"
                  disabled={view}
                  required
                  value={data.code}
                  onChange={(v) => set("code", v)}
                  error={errors["code"]}
                />
                <InputText
                  label="Printable name"
                  placeholder="Printable name"
                  disabled={view}
                  required
                  value={data.printable_name}
                  onChange={(v) => set("printable_name", v)}
                  error={errors["printable_name"]}
                />
                <InputTextBox
                  label="Description"
                  placeholder="Description"
                  disabled={view}
                  required
                  value={data.description}
                  onChange={(v) => set("description", v)}
                  error={errors["description"]}
                />
                <InputText
                  label="Document name"
                  placeholder="Document name"
                  disabled={view}
                  required
                  value={data.doc_name}
                  onChange={(v) => set("doc_name", v)}
                  error={errors["doc_name"]}
                />
                <InputSelect
                  label="Document type"
                  placeholder="Document type"
                  disabled={view}
                  required
                  value={idToname(data.doc_type, kyccert)}
                  options={kyccert ? kyccert.map((d) => d.printable_name) : []}
                  onChange={(v) => set("doc_type", nameToid(v, kyccert))}
                  error={errors["doc_type"]}
                />
              </div>
              <div className="formside">
                <InputText
                  label="Document number"
                  placeholder="Document number"
                  required
                  disabled={view}
                  value={data.doc_number}
                  onChange={(v) => set("doc_number", v)}
                  error={errors["doc_number"]}
                />
                <InputDate
                  label="Document expiry date"
                  placeholder="Document expiry date"
                  required
                  disabled={view}
                  value={data.doc_expiry}
                  onChange={(v) => set("doc_expiry", v)}
                  error={errors["doc_expiry"]}
                />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <InputFile
                    label="Upload document"
                    placeholder="Upload document"
                    required
                    disabled={view}
                    value={data.doc_file}
                    onChange={(v) => set("doc_file", v)}
                    error={errors["doc_file"]}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "small",
                      color: "#64646499",
                      alignSelf: "stretch",
                    }}
                  >
                    Format pdf, docx, png, jpg
                  </div>
                </div>
                <InputCheckBox
                  label="Verified"
                  color="#5AA100"
                  disabled={view}
                  value={data.is_verified}
                  onChange={(v) => set("is_verified", v)}
                />
              </div>
            </div>
            <div style={{ padding: "10px" }} />
          </div>
        </div>
        <div className="modal-actions">
          <div className="actions">
            <button
              className="btn-red"
              type="button"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </button>
            <button className="btn" type="submit">
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default NewPLSCertModal;
